import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useAuth, ROLE_LABELS } from '../../lib/auth';
import { LayoutDashboard, BarChart3, FolderOpen, Calendar, AlertTriangle, Users, Settings, Database, ChevronLeft, ChevronRight, LogOut, Shield, Layers, Share2, CheckCircle2, TrendingUp, Trophy, X, Radar } from 'lucide-react';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/t3J9pG9VM9s2YNfBdSCinK/Logo_Kementerian_Lingkungan_Hidup_-_Badan_Pengendalian_Lingkungan_Hidup_(2024).png';
interface SidebarProps {
  className?: string;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}
export function Sidebar({
  className,
  isMobileOpen = false,
  onMobileClose
}: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    logout,
    hasPermission
  } = useAuth();
  const navRef = useRef<HTMLDivElement>(null);
  const isActive = (path: string) => location.pathname === path;
  const handleLogout = () => {
    logout();
    navigate('/portal');
  };
  const menuGroups = [{
    title: 'MONITORING',
    items: [{
      name: 'Dashboard Nasional',
      path: '/command-center',
      icon: LayoutDashboard,
      permission: 'canViewDashboard' as const
    }, {
      name: 'Skoring Daerah',
      path: '/command-center/skoring',
      icon: BarChart3,
      permission: 'canViewDashboard' as const
    }, {
      name: 'Skor Kinerja',
      path: '/command-center/skor-kinerja',
      icon: Trophy,
      permission: 'canViewDashboard' as const
    }]
  }, {
    title: 'DISTRIBUSI KONTEN',
    items: [{
      name: 'Campaign Distribusi',
      path: '/command-center/distribusi',
      icon: Share2,
      permission: 'canManageContent' as const
    }, {
      name: 'Progress & Monitoring',
      path: '/command-center/distribusi/progress',
      icon: TrendingUp,
      permission: 'canManageContent' as const
    }, {
      name: 'Validasi Posting',
      path: '/command-center/distribusi/validasi',
      icon: CheckCircle2,
      permission: 'canManageContent' as const
    }, {
      name: 'Dashboard Sosmed',
      path: '/command-center/sosmed',
      icon: Users,
      permission: 'canManageContent' as const
    }]
  }, {
    title: 'PROGRAM & KONTEN',
    items: [{
      name: 'Katalog Program',
      path: '/command-center/katalog',
      icon: Layers,
      permission: 'canManageContent' as const
    }, {
      name: 'Bank Konten',
      path: '/command-center/konten',
      icon: FolderOpen,
      permission: 'canManageContent' as const
    }]
  }, {
    title: 'OPERASIONAL',
    items: [{
      name: 'Event Nasional',
      path: '/command-center/event',
      icon: Calendar,
      permission: 'canManageEvents' as const
    }, {
      name: 'Isu & Krisis',
      path: '/command-center/isu',
      icon: AlertTriangle,
      permission: 'canApproveReports' as const
    }, {
      name: 'Pemantauan Sosmed',
      path: '/command-center/pemantauan-sosmed',
      icon: Radar,
      permission: 'canApproveReports' as const
    }]
  }, {
    title: 'KEMITRAAN',
    items: [{
      name: 'Komunitas',
      path: '/command-center/komunitas',
      icon: Users,
      permission: 'canManageCommunities' as const
    }]
  }, {
    title: 'SISTEM',
    items: [{
      name: 'Asset Registry',
      path: '/command-center/assets',
      icon: Database,
      permission: 'canManageAssets' as const
    }, {
      name: 'Admin',
      path: '/command-center/admin',
      icon: Settings,
      permission: 'canManageUsers' as const
    }]
  }];
  // Filter menu items based on permissions
  const filteredMenuGroups = menuGroups.map(group => ({
    ...group,
    items: group.items.filter(item => hasPermission(item.permission))
  })).filter(group => group.items.length > 0);
  // Auto-scroll to active menu item on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      if (navRef.current) {
        const activeItem = navRef.current.querySelector('[data-active="true"]');
        if (activeItem) {
          activeItem.scrollIntoView({
            block: 'center',
            behavior: 'smooth'
          });
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname]);
  // Mobile Overlay Backdrop
  const MobileBackdrop = () => <div className={cn('fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-300', isMobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none')} onClick={onMobileClose} data-id="element-402" />;
  return <>
      <MobileBackdrop data-id="element-403" />
      <div className={cn('flex flex-col h-screen bg-primary-900 text-white transition-transform duration-300 border-r border-primary-800 will-change-transform',
    // Desktop classes
    'lg:relative lg:translate-x-0 lg:sticky lg:top-0', collapsed ? 'lg:w-16' : 'lg:w-60',
    // Mobile classes (Fixed drawer)
    'fixed inset-y-0 left-0 z-50 w-60 shadow-xl', isMobileOpen ? 'translate-x-0' : '-translate-x-full', className)} data-id="element-404">
        {/* Logo Area */}
        <div className="h-14 flex items-center justify-between px-3 border-b border-primary-800 flex-shrink-0 bg-primary-900" data-id="element-405">
          <Link to="/command-center" className="flex items-center gap-2.5 overflow-hidden" onClick={onMobileClose} data-id="element-406">
            <img src={LOGO_URL} alt="Logo KLHK" className="h-8 w-8 object-contain flex-shrink-0 bg-white rounded p-0.5" data-id="element-407" />
            {(!collapsed || isMobileOpen) && <div className="flex flex-col min-w-0" data-id="element-408">
                <span className="font-bold text-sm leading-tight truncate text-white" data-id="element-409">
                  Indonesia ASRI Hub
                </span>
                <span className="text-[9px] text-primary-400 tracking-wider truncate font-medium" data-id="element-410">
                  COMMAND CENTER
                </span>
              </div>}
          </Link>
          {/* Mobile Close Button */}
          <button onClick={onMobileClose} className="lg:hidden p-1 text-primary-400 hover:text-white" data-id="element-411">
            <X className="h-5 w-5" data-id="element-412" />
          </button>
        </div>

        {/* Navigation */}
        <div ref={navRef} className="flex-1 overflow-y-auto py-2 custom-scrollbar" data-id="element-413">
          {filteredMenuGroups.map((group, groupIndex) => <div key={groupIndex} className="mb-1.5 px-2" data-id="element-414">
              {(!collapsed || isMobileOpen) && <h3 className="text-[10px] font-semibold text-primary-400 uppercase tracking-wider mb-1 px-2" data-id="element-415">
                  {group.title}
                </h3>}
              <div className="space-y-px" data-id="element-416">
                {group.items.map(item => <Link key={item.path} to={item.path} onClick={onMobileClose} data-active={isActive(item.path)} className={cn('flex items-center gap-2.5 px-2.5 py-[6px] rounded-md text-[13px] transition-all duration-150', isActive(item.path) ? 'bg-primary-600 text-white font-medium shadow-sm ring-1 ring-primary-500' : 'text-primary-200 hover:bg-primary-800 hover:text-white')} title={collapsed && !isMobileOpen ? item.name : undefined} data-id="element-417">
                    <item.icon className="h-4 w-4 flex-shrink-0" data-id="element-418" />
                    {(!collapsed || isMobileOpen) && <span className="truncate" data-id="element-419">{item.name}</span>}
                  </Link>)}
              </div>
              {collapsed && !isMobileOpen && <div className="h-px bg-primary-800 mx-2 my-1" data-id="element-420" />}
            </div>)}
        </div>

        {/* User Profile & Collapse Toggle */}
        <div className="p-2.5 border-t border-primary-800 bg-primary-950 flex-shrink-0" data-id="element-421">
          <div className="flex items-center gap-2.5 overflow-hidden" data-id="element-422">
            <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center text-xs font-bold flex-shrink-0 text-white ring-1 ring-primary-500" data-id="element-423">
              {user?.name?.substring(0, 2).toUpperCase() || 'AD'}
            </div>
            {(!collapsed || isMobileOpen) && <div className="flex-1 min-w-0" data-id="element-424">
                <p className="text-xs font-medium truncate text-white" data-id="element-425">
                  {user?.name || 'Admin Pusat'}
                </p>
                <p className="text-[10px] text-primary-400 truncate flex items-center gap-1" data-id="element-426">
                  <Shield className="h-2.5 w-2.5" data-id="element-427" />
                  {user?.role ? ROLE_LABELS[user.role] : 'Super Admin'}
                </p>
              </div>}
            {(!collapsed || isMobileOpen) && <button onClick={handleLogout} className="text-primary-400 hover:text-red-400 transition-colors p-1" title="Keluar" data-id="element-428">
                <LogOut className="h-3.5 w-3.5" data-id="element-429" />
              </button>}
          </div>

          {/* Desktop Collapse Toggle - Hidden on Mobile */}
          <button onClick={() => setCollapsed(!collapsed)} className="hidden lg:flex w-full items-center justify-center p-1.5 mt-2 rounded-md bg-primary-800 hover:bg-primary-700 text-primary-400 hover:text-white transition-colors" data-id="element-430">
            {collapsed ? <ChevronRight className="h-4 w-4" data-id="element-431" /> : <ChevronLeft className="h-4 w-4" data-id="element-432" />}
          </button>
        </div>
      </div>
    </>;
}